import { useMemo } from 'react';
import { Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
} from '@/components/ui/combobox';
import { useAorText } from 'utils/aorTerminology';
import { useSitStatAorScope } from './SitStatAorScopeContext';
import { flattenAorHierarchy } from './sitstat-aor-scope';

type AorOption = { id: string | null; label: string; depth: number };

export function SitStatAorFilterBar() {
  const { scope, selectedAorId, setSelectedAorId, hierarchy } =
    useSitStatAorScope();
  const aorText = useAorText();
  const anchor = useComboboxAnchor();
  const options = useMemo(() => flattenAorHierarchy(hierarchy), [hierarchy]);
  const selectedKnown = options.some(({ node }) => node.id === selectedAorId);

  const items = useMemo<AorOption[]>(() => {
    const list: AorOption[] = [
      { id: null, label: aorText('All AORs'), depth: 0 },
    ];
    if (selectedAorId && !selectedKnown) {
      list.push({
        id: selectedAorId,
        label: scope?.selectedAorName ?? aorText('Selected AOR'),
        depth: 0,
      });
    }
    options.forEach(({ node, depth }) =>
      list.push({ id: node.id, label: node.name, depth })
    );
    return list;
  }, [aorText, options, scope?.selectedAorName, selectedAorId, selectedKnown]);
  const selectedItem =
    items.find((item) => item.id === (selectedAorId ?? null)) ?? items[0];

  return (
    <div
      className="tw:flex tw:shrink-0 tw:items-center tw:gap-2 tw:border-b tw:border-border tw:bg-background tw:px-3 tw:py-1.5 tw:text-foreground"
      data-testid="sitstat-aor-filter-bar"
      data-tour="sitstat-aor-filter-bar"
    >
      <Filter
        className="tw:h-3.5 tw:w-3.5 tw:text-muted-foreground"
        aria-hidden
      />
      <span className="tw:text-xs tw:font-medium tw:text-muted-foreground">
        {aorText('AOR')}
      </span>
      <div ref={anchor} className="tw:w-64">
        <Combobox
          items={items}
          value={selectedItem}
          onValueChange={(next) => setSelectedAorId(next?.id ?? null)}
          itemToStringLabel={(item) => item.label}
          isItemEqualToValue={(left, right) => left.id === right.id}
        >
          <ComboboxTrigger
            aria-label={aorText('Filter SitStat by AOR')}
            className="tw:flex tw:h-7 tw:w-full tw:items-center tw:justify-between tw:gap-1.5 tw:rounded-lg tw:border tw:border-input tw:bg-transparent tw:px-2.5 tw:text-xs tw:shadow-xs tw:dark:bg-input/30"
          >
            <span className="tw:min-w-0 tw:truncate">
              <ComboboxValue placeholder={aorText('All AORs')} />
            </span>
          </ComboboxTrigger>
          <ComboboxContent anchor={anchor} className="dark">
            <ComboboxInput
              placeholder={aorText('Search AORs')}
              showTrigger={false}
              aria-label={aorText('Search AORs')}
            />
            <ComboboxEmpty>{aorText('No AORs match.')}</ComboboxEmpty>
            <ComboboxList>
              {(item: AorOption) => (
                <ComboboxItem key={item.id ?? 'all'} value={item}>
                  <span
                    className="tw:truncate"
                    style={{ paddingLeft: `${item.depth * 0.75}rem` }}
                  >
                    {item.label}
                  </span>
                </ComboboxItem>
              )}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
      </div>
      {scope ? (
        <Button
          type="button"
          variant="ghost"
          size="xs"
          onClick={() => setSelectedAorId(null)}
          aria-label={aorText('Clear AOR filter')}
        >
          <X className="tw:h-3 tw:w-3" />
          Clear Filter
        </Button>
      ) : null}
    </div>
  );
}
